// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChainActivityTab } from "@/features/chains/chain-activity-tab";
import { ChainImportsTab } from "@/features/chains/chain-imports-tab";
import { ChainInfoTab } from "@/features/chains/chain-info-tab";
import { ChainRegionsTab } from "@/features/chains/chain-regions-tab";
import { ChainSettingsTab } from "@/features/chains/chain-settings-tab";
import { ChainStoresTab } from "@/features/chains/chain-stores-tab";
import { ChainUptimeTab } from "@/features/chains/chain-uptime-tab";
import { CutOffTimesTab } from "@/features/cut-off-times/cut-off-times-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

export const Route = createFileRoute("/_authenticated/chains/$chainName/")({
  // The Edit tab moved to `/chains/$chainName/edit`. Old links and bookmarks carrying `?t=edit`
  // are forwarded rather than silently dropped to Info.
  beforeLoad: ({ params, search }) => {
    if (search.t === "edit") throw redirect({ to: "/chains/$chainName/edit", params, search: {} });
  },
  component: ChainDetailPage,
});

function ChainDetailPage() {
  const { chainName } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const navigate = useNavigate();
  const { t } = useTranslation("back-office");

  const canViewRegions = hasPermission(PAGE_PERMISSIONS.chainsRegions);
  const canViewImports = hasPermission(PAGE_PERMISSIONS.ingressImports);
  const canViewSettings = hasPermission(PAGE_PERMISSIONS.chainsSettings);
  const canViewCutOffTimes = hasPermission(PAGE_PERMISSIONS.chainsCutOffTimes);
  const canViewUptime = hasPermission(PAGE_PERMISSIONS.chainsUptime);
  // Activity log is work-in-progress — debug-mode only (in addition to the view permission).
  const canViewActivity = hasPermission(PAGE_PERMISSIONS.chainsActivity) && debugMode;
  const tabAllowed: Record<string, boolean> = {
    info: true,
    stores: true,
    regions: canViewRegions,
    "cut-off-times": canViewCutOffTimes,
    uptime: canViewUptime,
    imports: canViewImports,
    settings: canViewSettings,
    activity: canViewActivity,
  };
  const requestedTab = tab ?? "info";
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  function handleTabChange(tab: string) {
    navigate({
      to: "/chains/$chainName",
      params: { chainName },
      search: tab === "info" ? {} : { t: tab },
      replace: true,
    });
  }

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
      <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
        <TabsList>
          <TabsIndicator />
          <TabsTrigger value="info">{t("chains.tabs.info")}</TabsTrigger>
          {canViewRegions && <TabsTrigger value="regions">{t("chains.tabs.regions")}</TabsTrigger>}
          <TabsTrigger value="stores">{t("chains.tabs.stores")}</TabsTrigger>
          {canViewCutOffTimes && <TabsTrigger value="cut-off-times">{t("chains.tabs.cutOffTimes")}</TabsTrigger>}
          {canViewUptime && <TabsTrigger value="uptime">{t("chains.tabs.uptime")}</TabsTrigger>}
          {canViewImports && <TabsTrigger value="imports">{t("chains.tabs.imports")}</TabsTrigger>}
          {canViewSettings && <TabsTrigger value="settings">{t("chains.tabs.settings")}</TabsTrigger>}
          {canViewActivity && <TabsTrigger value="activity">{t("chains.tabs.activityLog")}</TabsTrigger>}
        </TabsList>
      </div>
      <TabsContent value="info" className="space-y-4">
        <ChainInfoTab chainName={chainName} />
      </TabsContent>
      {canViewSettings && (
        <TabsContent value="settings" className="space-y-4">
          <ChainSettingsTab chainName={chainName} initialFilters={activeTab === "settings" ? f : undefined} />
        </TabsContent>
      )}
      {canViewRegions && (
        <TabsContent value="regions" className="space-y-4">
          <ChainRegionsTab chainName={chainName} />
        </TabsContent>
      )}
      <TabsContent value="stores" className="space-y-4">
        <ChainStoresTab chainName={chainName} initialFilters={activeTab === "stores" ? f : undefined} />
      </TabsContent>
      {canViewUptime && (
        <TabsContent value="uptime" className="space-y-4">
          <ChainUptimeTab chainName={chainName} initialFilters={activeTab === "uptime" ? f : undefined} />
        </TabsContent>
      )}
      {canViewImports && (
        <TabsContent value="imports" className="space-y-4">
          <ChainImportsTab chainName={chainName} initialFilters={activeTab === "imports" ? f : undefined} />
        </TabsContent>
      )}
      {canViewCutOffTimes && (
        <TabsContent value="cut-off-times" className="space-y-4">
          <CutOffTimesTab
            target={{ kind: "chain", chainName }}
            initialFilters={activeTab === "cut-off-times" ? f : undefined}
          />
        </TabsContent>
      )}
      {canViewActivity && (
        <TabsContent value="activity" className="space-y-4">
          <ChainActivityTab chainName={chainName} initialFilters={activeTab === "activity" ? f : undefined} />
        </TabsContent>
      )}
    </Tabs>
  );
}
